import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { notificationService } from '../services/notificationService';
import websocketService from '../services/websocket/websocketService';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const NotificationsContext = createContext();

export const useNotifications = () => {
    const context = useContext(NotificationsContext);
    if (!context) {
        throw new Error('useNotifications must be used within a NotificationsProvider');
    }
    return context;
};

export const NotificationsProvider = ({ children }) => {
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const { user } = useAuth();
    const wsSubscriptionId = useRef(null);

    const fetchNotifications = async () => {
        const data = await notificationService.getNotifications();
        setNotifications(data);
        setUnreadCount(data.filter(n => !n.read).length);
        setLoading(false);
    };

    const markAsRead = async (notificationId) => {
        const success = await notificationService.markAsRead(notificationId);
        if (success) {
            setNotifications(prev => prev.map(n =>
                n.id === notificationId ? { ...n, read: true } : n
            ));
            setUnreadCount(prev => Math.max(0, prev - 1));
        }
    };

    const markAllAsRead = async () => {
        const success = await notificationService.markAllAsRead();
        if (success) {
            setNotifications(prev => prev.map(n => ({ ...n, read: true })));
            setUnreadCount(0);
        }
    };

    // Handle incoming WebSocket notification
    const handleWebSocketNotification = useCallback((notification) => {
        // Prepend new notification to the list
        setNotifications(prev => {
            // Avoid duplicates if polling already fetched it
            if (prev.some(n => n.id === notification.id)) {
                return prev;
            }
            return [notification, ...prev];
        });
        setUnreadCount(prev => prev + 1);

        // Show toast notification
        const toastType = notification.type?.includes('FAILED') ? 'error'
            : notification.type?.includes('SUCCESS') || notification.type?.includes('COMPLETED') ? 'success'
            : 'default';

        const toastMessage = notification.title || notification.message;
        if (toastType === 'error') {
            toast.error(toastMessage, { duration: 5000 });
        } else if (toastType === 'success') {
            toast.success(toastMessage, { duration: 4000 });
        } else {
            toast(toastMessage, { duration: 4000 });
        }
    }, []);

    // Polling fallback
    useEffect(() => {
        fetchNotifications();
        const interval = setInterval(fetchNotifications, 60000);
        return () => clearInterval(interval);
    }, []);

    // WebSocket real-time subscription
    useEffect(() => {
        if (!user?.id) return;

        const setupWebSocket = async () => {
            try {
                await websocketService.connect();
                wsSubscriptionId.current = websocketService.subscribeToNotifications(
                    user.id,
                    handleWebSocketNotification
                );
            } catch (error) {
                // WebSocket connection failed — polling fallback is still active
            }
        };

        setupWebSocket();

        return () => {
            if (wsSubscriptionId.current) {
                websocketService.unsubscribe(wsSubscriptionId.current);
                wsSubscriptionId.current = null;
            }
        };
    }, [user?.id, handleWebSocketNotification]);

    const value = {
        notifications,
        unreadCount,
        loading,
        markAsRead,
        markAllAsRead,
        refresh: fetchNotifications
    };

    return (
        <NotificationsContext.Provider value={value}>
            {children}
        </NotificationsContext.Provider>
    );
};

export default NotificationsContext;
